import React, {useCallback, useState, type FC} from 'react'

import type {OmicsCodeHeaderProps} from './omicsMarkdownTypes'

const useCopyToClipboard = (copiedDuration = 3000) => {
  const [isCopied, setIsCopied] = useState(false)
  const copyToClipboard = (value: string) => {
    if (!value) return
    void navigator.clipboard.writeText(value).then(() => {
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), copiedDuration)
    })
  }
  return {isCopied, copyToClipboard}
}

function extensionForLanguage(language: string | undefined): string {
  const lang = (language || '').toLowerCase()
  if (lang === 'python' || lang === 'py') return 'py'
  if (lang === 'r') return 'R'
  if (lang === 'json') return 'json'
  if (lang === 'bash' || lang === 'sh' || lang === 'shell') return 'sh'
  if (lang === 'javascript' || lang === 'js') return 'js'
  if (lang === 'typescript' || lang === 'ts') return 'ts'
  return 'txt'
}

export function downloadCodeAsFile(code: string, language: string | undefined): void {
  if (!code) return
  const ext = extensionForLanguage(language)
  const blob = new Blob([code], {type: 'text/plain;charset=utf-8'})
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `script.${ext}`
  anchor.click()
  URL.revokeObjectURL(url)
}

export const OmicsCodeBlockHeader: FC<OmicsCodeHeaderProps> = ({language, code}) => {
  const {isCopied, copyToClipboard} = useCopyToClipboard()
  const onCopy = useCallback(() => {
    if (!code || isCopied) return
    copyToClipboard(code)
  }, [code, copyToClipboard, isCopied])
  const onDownload = useCallback(() => {
    downloadCodeAsFile(code, language)
  }, [code, language])

  return (
    <div className='omics-aui-code-header d-flex align-items-center justify-content-between border-bottom px-2 py-1 small bg-light'>
      <span className='text-muted text-lowercase'>{language || 'code'}</span>
      <div className='omics-aui-code-header-actions d-flex align-items-center gap-1'>
        <button
          type='button'
          className='btn btn-sm btn-link py-0 px-1 omics-aui-code-header-btn'
          onClick={onDownload}
          title='Download script'
          aria-label='Download script'
        >
          <i className='fas fa-download' aria-hidden />
        </button>
        <button
          type='button'
          className='btn btn-sm btn-link py-0 px-1 omics-aui-code-header-btn'
          onClick={onCopy}
          title={isCopied ? 'Copied' : 'Copy code'}
          aria-label={isCopied ? 'Copied' : 'Copy code'}
        >
          <i className={`fas ${isCopied ? 'fa-check' : 'fa-copy'}`} aria-hidden />
        </button>
      </div>
    </div>
  )
}
